import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

import type {
  CommonCodeItem,
  CommonCodeCreatePayload,
  CommonCodeUpdatePayload,
} from "src/types/common/commonCodeTypes";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  grpCode: string;
  item?: CommonCodeItem | null;
  loading?: boolean;
  onClose: () => void;
  onSubmitCreate: (payload: CommonCodeCreatePayload) => Promise<boolean>;
  onSubmitEdit: (payload: CommonCodeUpdatePayload) => Promise<boolean>;
};

const emptyCreate = (grpCode: string): CommonCodeCreatePayload => ({
  grpCode,
  code: "",
  codeName: "",
  description: "",
  useYn: true,
});

export default function CommonCodeUpsertModal({
  open,
  mode,
  grpCode,
  item,
  loading,
  onClose,
  onSubmitCreate,
  onSubmitEdit,
}: Props) {
  const [createPayload, setCreatePayload] = useState<CommonCodeCreatePayload>(emptyCreate(grpCode));
  const [updatePayload, setUpdatePayload] = useState<CommonCodeUpdatePayload>({
    codeId: 0,
    codeName: "",
    description: "",
    codeOrder: 0,
    useYn: true,
  });

  useEffect(() => {
    if (!open) return;

    if (mode === "create") {
      setCreatePayload(emptyCreate(grpCode));
      return;
    }

    if (mode === "edit" && item) {
      setUpdatePayload({
        codeId: item.codeId,
        codeName: item.codeName ?? "",
        description: item.description ?? "",
        codeOrder: item.codeOrder ?? 0,
        useYn: !!item.useYn,
      });
    }
  }, [open, mode, item, grpCode]);

  const validateCreate = () => {
    if (!grpCode) return "그룹을 먼저 선택해주세요.";
    if (!createPayload.code.trim()) return "code를 입력해주세요.";
    if (!createPayload.codeName.trim()) return "codeName을 입력해주세요.";
    return null;
  };

  const validateEdit = () => {
    if (!updatePayload.codeId) return "codeId가 없습니다.";
    if (!updatePayload.codeName.trim()) return "codeName을 입력해주세요.";
    return null;
  };

  const handleSubmit = async () => {
    if (mode === "create") {
      const err = validateCreate();
      if (err) return window.alert(err);
      const ok = await onSubmitCreate(createPayload);
      if (ok) onClose();
      return;
    }

    const err = validateEdit();
    if (err) return window.alert(err);
    const ok = await onSubmitEdit(updatePayload);
    if (ok) onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === "create" ? "코드 생성" : "코드 수정"}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {mode === "create" ? (
            <>
              <TextField label="grpCode" value={grpCode} disabled />
              <TextField
                label="code"
                value={createPayload.code}
                onChange={(e) => setCreatePayload((p) => ({ ...p, code: e.target.value }))}
              />
              <TextField
                label="codeName"
                value={createPayload.codeName}
                onChange={(e) => setCreatePayload((p) => ({ ...p, codeName: e.target.value }))}
              />
              <TextField
                label="description"
                value={createPayload.description}
                onChange={(e) => setCreatePayload((p) => ({ ...p, description: e.target.value }))}
                multiline
                minRows={3}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createPayload.useYn}
                    onChange={(e) => setCreatePayload((p) => ({ ...p, useYn: e.target.checked }))}
                  />
                }
                label="useYn"
              />
            </>
          ) : (
            <>
              <TextField label="codeId" value={updatePayload.codeId} disabled />
              <TextField
                label="codeName"
                value={updatePayload.codeName}
                onChange={(e) => setUpdatePayload((p) => ({ ...p, codeName: e.target.value }))}
              />
              <TextField
                label="description"
                value={updatePayload.description}
                onChange={(e) => setUpdatePayload((p) => ({ ...p, description: e.target.value }))}
                multiline
                minRows={3}
              />
              <TextField
                label="codeOrder"
                type="number"
                value={updatePayload.codeOrder}
                onChange={(e) =>
                  setUpdatePayload((p) => ({ ...p, codeOrder: Number(e.target.value) }))
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={updatePayload.useYn}
                    onChange={(e) => setUpdatePayload((p) => ({ ...p, useYn: e.target.checked }))}
                  />
                }
                label="useYn"
              />
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          취소
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {mode === "create" ? "생성" : "수정"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
